import axios from "axios";
import { toast } from "react-toastify";

export const SESSION_EXPIRED_EVENT = "agilityhr:session-expired";

const DEFAULT_IDLE_TIMEOUT_MS = 30 * 60 * 1000;
const TOKEN_STORAGE_KEYS = ["token", "keycloak_id_token"];

let axiosInterceptorsInstalled = false;

export const getStoredToken = () => {
  return localStorage.getItem("token")?.replace(/^"|"$/g, "") || null;
};

export const getIdleTimeoutMs = () => {
  const configuredTimeout = Number(process.env.REACT_APP_IDLE_TIMEOUT_MS);
  return Number.isFinite(configuredTimeout) && configuredTimeout > 0
    ? configuredTimeout
    : DEFAULT_IDLE_TIMEOUT_MS;
};

export const decodeJwtPayload = (token) => {
  if (!token || token.split(".").length < 2) {
    return null;
  }

  try {
    const base64 = token
      .split(".")[1]
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(Math.ceil(token.split(".")[1].length / 4) * 4, "=");

    const decodedPayload = globalThis.atob(base64);
    return JSON.parse(decodedPayload);
  } catch (_error) {
    return null;
  }
};

export const getTokenExpiration = (token) => {
  const payload = decodeJwtPayload(token);
  return typeof payload?.exp === "number" ? payload.exp * 1000 : null;
};

export const isTokenExpired = (token, now = Date.now()) => {
  if (!token) {
    return true;
  }

  const expiresAt = getTokenExpiration(token);
  return expiresAt ? expiresAt <= now : false;
};

export const clearSessionStorage = () => {
  TOKEN_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
};

export const expireSession = (reason = "expired") => {
  clearSessionStorage();

  window.dispatchEvent(
    new CustomEvent(SESSION_EXPIRED_EVENT, { detail: { reason } }),
  );

  if (window.location.pathname !== "/login") {
    window.location.assign("/login");
  }
};

export const setupAxiosSessionInterceptor = () => {
  if (axiosInterceptorsInstalled) {
    return;
  }

  axiosInterceptorsInstalled = true;

  axios.interceptors.request.use((config) => {
    const token = getStoredToken();

    if (token && isTokenExpired(token)) {
      expireSession("expired");
      const error = new Error("session_expired");
      error.isSessionExpired = true;
      return Promise.reject(error);
    }

    if (token && !config.headers?.Authorization) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      };
    }

    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error?.response?.status === 401) {
        expireSession("unauthorized");
      }

      if (error?.response?.status === 403) {
        const detail = error.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : "Permission denied");
      }

      return Promise.reject(error);
    },
  );
};
